import { useState, type ReactNode } from 'react';
import { Icon, StateBox, type IconName } from '@garnet/ui';
import type { VerticalConfig } from '@garnet/config';
import type { GarnetApi } from '../../data/types.js';
import type { Route } from '../../nav.js';
import { useAsync } from '../../useAsync.js';
import { AdvisorMark } from '../../components/advisor.js';
import { Intake } from './Intake.js';
import { Recs } from './Recs.js';
import { Triage } from './Triage.js';
import { Ledger } from './Ledger.js';
import { Copilot } from './Copilot.js';

/* Garnet · AI Advisor — shell: a rail of four surfaces (Intake / In context /
   Triage / Ledger) and a right-docked Copilot. The Advisor recommends, never
   decides — every surface records the human response. */

type SurfaceKey = 'intake' | 'recs' | 'triage' | 'ledger';
interface SurfaceDef {
  k: SurfaceKey;
  label: string;
  icon: IconName;
  count?: number;
}

const SURFACES: SurfaceDef[] = [
  { k: 'intake', label: 'Intake', icon: 'upload' },
  { k: 'recs', label: 'In context', icon: 'chat' },
  { k: 'triage', label: 'Triage', icon: 'filter', count: 4 },
  { k: 'ledger', label: 'Ledger', icon: 'file', count: 8 },
];

export function Advisor({
  api,
  config,
  go,
}: {
  api: GarnetApi;
  config: VerticalConfig;
  go: (r: Route) => void;
}) {
  const { data, loading, error } = useAsync(() => api.getAdvisorData(), []);
  const [route, setRoute] = useState<SurfaceKey>('intake');
  const [copilot, setCopilot] = useState(true);

  if (loading) return <StateBox kind="loading" title="Loading the Advisor…" />;
  if (error || !data) return <StateBox kind="error" title="Could not load the Advisor">{error}</StateBox>;

  let surface: ReactNode;
  if (route === 'intake') surface = <Intake d={data.intake} />;
  else if (route === 'recs') surface = <Recs recs={data.recs} />;
  else if (route === 'triage') surface = <Triage triage={data.triage} go={go} />;
  else surface = <Ledger ledger={data.ledger} />;

  return (
    <div className="adv-shell">
      <div className="adv-stage">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 14,
            flexWrap: 'wrap',
          }}
        >
          <div className="adv-principle" style={{ marginBottom: 0, flex: 1, minWidth: 240 }}>
            <span className="ap-mark">
              <AdvisorMark size={14} />
            </span>
            <span>
              The Advisor <b>recommends, never decides</b>. Every recommendation is grounded and its confidence stated —
              the human accepts, modifies, or rejects, and the response is recorded.
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}>
            <button className={'cop-toggle' + (copilot ? ' on' : '')} onClick={() => setCopilot((c) => !c)}>
              <span className="cd" />
              Copilot
            </button>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: 'var(--mono)',
                fontSize: 11,
                color: 'var(--ink)',
                border: '1px solid var(--rule)',
                borderRadius: 'var(--radius-md)',
                padding: '5px 10px',
                background: 'var(--panel)',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--verified)', flex: 'none' }} />
              {config.display_name} · EU residency
            </div>
          </div>
        </div>

        <div className="adv-rail" role="tablist">
          {SURFACES.map((s) => (
            <button key={s.k} role="tab" aria-pressed={route === s.k} onClick={() => setRoute(s.k)}>
              <Icon name={s.icon} size={13} />
              {s.label}
              {s.count ? <span className="rc">{s.count}</span> : null}
            </button>
          ))}
        </div>

        {surface}
      </div>

      {copilot && <Copilot prompts={data.copPrompts} answers={data.copAnswers} onClose={() => setCopilot(false)} />}
    </div>
  );
}
